import React from 'react';
import { Search, Bell, Edit2, Coins, FileText, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { TabsContent } from '../../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';

const ContentTab = ({
    filteredContent, contentSearch, setContentSearch,
    contentTypeFilter, setContentTypeFilter,
    contentStatusFilter, setContentStatusFilter,
    contentSortBy, setContentSortBy,
    dateRangeFilter, setDateRangeFilter,
    priceRangeFilter, setPriceRangeFilter,
    unreadNotifications, fetchNotifications,
    handleReview, handleSetPrice, handleEdit,
    getItemIcon, getItemTitle
}) => (
    <TabsContent value="content">
        <Card>
            <CardHeader>
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <CardTitle>All Content</CardTitle>
                            <CardDescription>Manage all platform content</CardDescription>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={fetchNotifications}
                            className="relative"
                        >
                            <Bell className="h-4 w-4 mr-2" />
                            Notifications
                            {unreadNotifications > 0 && (
                                <Badge className="ml-2 bg-red-500 text-white px-2 py-0">{unreadNotifications}</Badge>
                            )}
                        </Button>
                    </div>

                    {/* Advanced Search and Filter UI */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                        {/* Search Box */}
                        <div className="lg:col-span-2">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                    placeholder="Search by title, description, city, subjects..."
                                    value={contentSearch}
                                    onChange={(e) => setContentSearch(e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                        </div>

                        {/* Type Filter */}
                        <Select value={contentTypeFilter} onValueChange={setContentTypeFilter}>
                            <SelectTrigger>
                                <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Types</SelectItem>
                                <SelectItem value="job">Jobs</SelectItem>
                                <SelectItem value="tuition">Tuitions</SelectItem>
                                <SelectItem value="tutor">Tutors</SelectItem>
                                <SelectItem value="organization">Organizations</SelectItem>
                            </SelectContent>
                        </Select>

                        {/* Status Filter */}
                        <Select value={contentStatusFilter} onValueChange={setContentStatusFilter}>
                            <SelectTrigger>
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Status</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="approved">Approved</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                        </Select>

                        {/* Sort By */}
                        <Select value={contentSortBy} onValueChange={setContentSortBy}>
                            <SelectTrigger>
                                <SelectValue placeholder="Sort by" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="date_desc">Newest First</SelectItem>
                                <SelectItem value="date_asc">Oldest First</SelectItem>
                                <SelectItem value="price_desc">Highest Price</SelectItem>
                                <SelectItem value="price_asc">Lowest Price</SelectItem>
                                <SelectItem value="views_desc">Most Viewed</SelectItem>
                                <SelectItem value="name_asc">A-Z</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Advanced Filters Row */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-2 border-t">
                        <div className="space-y-1">
                            <Label className="text-xs text-gray-500">Date From</Label>
                            <Input
                                type="date"
                                value={dateRangeFilter.start}
                                onChange={(e) => setDateRangeFilter({ ...dateRangeFilter, start: e.target.value })}
                                className="text-sm"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs text-gray-500">Date To</Label>
                            <Input
                                type="date"
                                value={dateRangeFilter.end}
                                onChange={(e) => setDateRangeFilter({ ...dateRangeFilter, end: e.target.value })}
                                className="text-sm"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs text-gray-500">Min Price (coins)</Label>
                            <Input
                                type="number"
                                placeholder="0"
                                value={priceRangeFilter.min}
                                onChange={(e) => setPriceRangeFilter({ ...priceRangeFilter, min: e.target.value })}
                                className="text-sm"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs text-gray-500">Max Price (coins)</Label>
                            <Input
                                type="number"
                                placeholder="1000"
                                value={priceRangeFilter.max}
                                onChange={(e) => setPriceRangeFilter({ ...priceRangeFilter, max: e.target.value })}
                                className="text-sm"
                            />
                        </div>
                    </div>

                    {/* Clear Filters Button */}
                    {(contentSearch || dateRangeFilter.start || dateRangeFilter.end || priceRangeFilter.min || priceRangeFilter.max) && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setContentSearch('');
                                setDateRangeFilter({ start: '', end: '' });
                                setPriceRangeFilter({ min: '', max: '' });
                                toast.success('Filters cleared');
                            }}
                            className="w-fit"
                        >
                            <XCircle className="h-4 w-4 mr-2" />
                            Clear All Filters
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent>
                <div className="mb-4 text-sm text-gray-500">
                    Showing {filteredContent.length} items
                </div>
                {filteredContent.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                        <p>No content found matching your filters</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filteredContent.map((item) => (
                            <div
                                key={`${item.type}-${item.id}`}
                                className={`border rounded-lg p-4 hover:shadow-md transition-all ${item.review_status === 'approved' ? 'border-l-4 border-l-green-400' :
                                    item.review_status === 'rejected' ? 'border-l-4 border-l-red-400' : 'border-l-4 border-l-yellow-400'
                                    }`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start space-x-4">
                                        <div className={`p-2 rounded-lg ${item.type === 'job' ? 'bg-blue-100' :
                                            item.type === 'tuition' ? 'bg-green-100' :
                                                item.type === 'tutor' ? 'bg-purple-100' : 'bg-orange-100'
                                            }`}>
                                            {getItemIcon(item.type)}
                                        </div>
                                        <div>
                                            <div className="flex items-center space-x-2 mb-1">
                                                <h3 className="font-semibold">{getItemTitle(item)}</h3>
                                                <Badge variant="secondary" className="capitalize">{item.type}</Badge>
                                                <Badge className={
                                                    item.review_status === 'approved' ? 'bg-green-100 text-green-700' :
                                                        item.review_status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                                                }>
                                                    {item.review_status || 'pending'}
                                                </Badge>
                                            </div>
                                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                                                <span className="flex items-center">
                                                    <Coins className="h-4 w-4 mr-1 text-yellow-500" />
                                                    {item.unlock_price || 0} coins
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex space-x-2">
                                        {item.review_status === 'pending' && (
                                            <Button size="sm" onClick={() => handleReview(item, item.type)}>
                                                Review
                                            </Button>
                                        )}
                                        <Button size="sm" variant="outline" onClick={() => handleEdit(item, item.type)} title="Edit Content">
                                            <Edit2 className="h-4 w-4" />
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => handleSetPrice(item)} title="Set Price">
                                            <Coins className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    </TabsContent>
);

export default ContentTab;
